import { defineStore } from 'pinia'
import axios from 'axios'
import { useAuthStore } from './auth'

export const usePropertiesStore = defineStore('properties', {
  state: () => ({
    properties: [],
    currentProperty: null,
    loading: false,
    error: null,
    statistics: {
      total: 0,
      paid: 0,
      unpaid: 0
    },
    filters: {
      paymentStatus: null,
      minPrice: null,
      maxPrice: null,
      location: null
    }
  }),
  
  getters: {
    filteredProperties: (state) => {
      let result = [...state.properties]
      
      if (state.filters.paymentStatus !== null) {
        result = result.filter(prop => prop.payment_status === state.filters.paymentStatus)
      }
      
      if (state.filters.minPrice !== null) {
        result = result.filter(prop => prop.price >= state.filters.minPrice)
      }
      
      if (state.filters.maxPrice !== null) {
        result = result.filter(prop => prop.price <= state.filters.maxPrice)
      }
      
      if (state.filters.location) {
        result = result.filter(prop => 
          prop.location.toLowerCase().includes(state.filters.location.toLowerCase())
        )
      }
      
      return result
    },
    
    paidProperties: (state) => {
      return state.properties.filter(prop => prop.payment_status === 'paid')
    },
    
    unpaidProperties: (state) => {
      return state.properties.filter(prop => prop.payment_status === 'unpaid')
    },
    
    propertyStatistics: (state) => {
      return {
        total: state.properties.length,
        paid: state.properties.filter(p => p.payment_status === 'paid').length,
        unpaid: state.properties.filter(p => p.payment_status === 'unpaid').length
      }
    }
  },
  
  actions: {
    async fetchProperties() {
      const authStore = useAuthStore()
      if (!authStore.token) return
      
      try {
        this.loading = true
        this.error = null
        
        const response = await axios.get('/api/properties', {
          headers: {
            Authorization: `Bearer ${authStore.token}`
          }
        })
        
        // Extract properties from the API response
        if (response.data && response.data.success) {
          this.properties = response.data.properties || []
        } else {
          this.properties = []
        }
      } catch (error) {
        this.error = error.response?.data?.message || 'Failed to fetch properties'
        console.error('Error fetching properties:', error)
      } finally {
        this.loading = false
      }
    },
    
    async fetchPropertyById(id) {
      const authStore = useAuthStore()
      if (!authStore.token) return
      
      try {
        this.loading = true
        this.error = null
        
        const response = await axios.get(`/api/properties/${id}`, {
          headers: {
            Authorization: `Bearer ${authStore.token}`
          }
        })
        
        // Extract property from the API response
        if (response.data && response.data.success) {
          this.currentProperty = response.data.property || null
          return response.data.property
        }
        return null
      } catch (error) {
        this.error = error.response?.data?.message || 'Failed to fetch property'
        console.error(`Error fetching property ${id}:`, error)
      } finally {
        this.loading = false
      }
    },
    
    async addProperty(propertyData) {
      const authStore = useAuthStore()
      if (!authStore.token) return
      
      try {
        this.loading = true
        this.error = null
        
        const response = await axios.post('/api/properties', propertyData, {
          headers: {
            Authorization: `Bearer ${authStore.token}`,
            'Content-Type': 'multipart/form-data'
          }
        })
        
        // Extract property from the API response and add to the list
        if (response.data && response.data.success && response.data.property) {
          this.properties.push(response.data.property)
          return response.data.property
        }
        return null
      } catch (error) {
        this.error = error.response?.data?.message || 'Failed to add property'
        console.error('Error adding property:', error)
        throw error
      } finally {
        this.loading = false
      }
    },
    
    async updateProperty(id, propertyData) {
      const authStore = useAuthStore()
      if (!authStore.token) return
      
      try {
        this.loading = true
        this.error = null
        
        const response = await axios.put(`/api/properties/${id}`, propertyData, {
          headers: {
            Authorization: `Bearer ${authStore.token}`,
            'Content-Type': 'multipart/form-data'
          }
        })
        
        // Extract property from the API response and update in the list
        if (response.data && response.data.success && response.data.property) {
          const index = this.properties.findIndex(p => p.id === id)
          if (index !== -1) {
            this.properties[index] = response.data.property
          }
          return response.data.property
        }
        return null
      } catch (error) {
        this.error = error.response?.data?.message || 'Failed to update property'
        console.error(`Error updating property ${id}:`, error)
        throw error
      } finally {
        this.loading = false
      }
    },
    
    async deleteProperty(id) {
      const authStore = useAuthStore()
      if (!authStore.token) return
      
      try {
        this.loading = true
        this.error = null
        
        const response = await axios.delete(`/api/properties/${id}`, {
          headers: {
            Authorization: `Bearer ${authStore.token}`
          }
        })
        
        // Check if delete was successful
        if (response.data && response.data.success) {
          // Remove the property from the list
          this.properties = this.properties.filter(p => p.id !== id)
          return true
        }
        return false
      } catch (error) {
        this.error = error.response?.data?.message || 'Failed to delete property'
        console.error(`Error deleting property ${id}:`, error)
        throw error
      } finally {
        this.loading = false
      }
    },
    
    setFilters(filters) {
      this.filters = { ...this.filters, ...filters }
    },
    
    clearFilters() {
      this.filters = {
        paymentStatus: null,
        minPrice: null,
        maxPrice: null,
        location: null
      }
    },
    
    async fetchStatistics() {
      const authStore = useAuthStore()
      if (!authStore.token) return
      
      try {
        this.loading = true
        this.error = null
        
        const response = await axios.get('/api/properties/statistics', {
          headers: {
            Authorization: `Bearer ${authStore.token}`
          }
        })
        
        // Extract statistics from the API response
        if (response.data && response.data.success && response.data.statistics) {
          this.statistics = response.data.statistics
        }
      } catch (error) {
        console.error('Error fetching property statistics:', error)
      } finally {
        this.loading = false
      }
    }
  }
})
